export type View = "calendar" | "vessels" | "settings";

export interface HashState {
  view: View;
  params: Record<string, string[]>;
}

const VIEWS: readonly View[] = ["calendar", "vessels", "settings"];

function isView(s: string): s is View {
  return (VIEWS as readonly string[]).includes(s);
}

// `decodeURIComponent` doesn't decode '+' to space; we serialise spaces as
// '+' (matching the existing app's URLs), so substitute on the way in.
function decodeQuery(s: string): string {
  return decodeURIComponent(s.replace(/\+/g, "%20"));
}

export function parseHash(hash: string): HashState {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return { view: "calendar", params: {} };

  const [pathPart, queryPart = ""] = raw.split("?", 2);
  const pathSegment = (pathPart ?? "").startsWith("/")
    ? (pathPart ?? "").slice(1)
    : (pathPart ?? "");
  const view: View = isView(pathSegment) ? pathSegment : "calendar";

  const params: Record<string, string[]> = {};
  if (queryPart) {
    for (const pair of queryPart.split("&")) {
      if (!pair) continue;
      const eq = pair.indexOf("=");
      const key = eq === -1 ? decodeQuery(pair) : decodeQuery(pair.slice(0, eq));
      const value = eq === -1 ? "" : decodeQuery(pair.slice(eq + 1));
      const existing = params[key];
      if (existing) existing.push(value);
      else params[key] = [value];
    }
  }

  return { view, params };
}

export function serializeHash(state: HashState): string {
  const queryParts: string[] = [];
  for (const [key, values] of Object.entries(state.params)) {
    for (const value of values) {
      const encodedKey = encodeURIComponent(key).replace(/%20/g, "+");
      const encodedValue = encodeURIComponent(value).replace(/%20/g, "+");
      queryParts.push(`${encodedKey}=${encodedValue}`);
    }
  }
  const query = queryParts.length ? `?${queryParts.join("&")}` : "";
  return `#/${state.view}${query}`;
}
